/**
 * @file OrderChainNode.tsx
 * @description Строка документа в «Пути заказа»
 * (phase19.24-order-card-chain-design.md §3, §8).
 *
 * Анатомия: рейл → название и номер → бейдж статуса → время и кто → вторая
 * строка (подсказка, прогресс, сумма, трек). Вся строка — одна ссылка и один
 * тач-таргет: ≥56px на телефоне, ≥48px на desktop.
 *
 * Типографика — как у строки документа, а не заголовка (admin-design-system
 * §3.2): название `text-sm font-medium` (ритм `DocumentItemsTable`), номер
 * моно `text-sm tabular-nums`, подсказка и время `text-xs`. На `text-base`
 * пять узлов подряд читались как пять заголовков и перебивали шапку карточки.
 *
 * Узел без номера и без страницы (платёж, отгрузка, возврат) ведёт во вкладку
 * карточки с подсветкой строки — мёртвого серого текста в пути не бывает.
 * Недоступный источник (склад не ответил) остаётся строкой с кнопкой
 * «Повторить»: терять узел нельзя, иначе менеджер решит, что задания нет.
 */

import { ChevronRight, TriangleAlert, X } from "lucide-react";
import { Link } from "react-router";
import type { ReactNode } from "react";

import type { OrderChainNodeDto } from "../../../lib/order-chain";
import {
  nodeDetailLine,
  nodeOwner,
  nodeProgressRatio,
  nodeTimeLine,
} from "../../../lib/order-chain-node-view";
import { ownerMeta } from "../../../lib/order-chain-owner";
import { nodeStateMeta, resolveNodeTone } from "../../../lib/order-chain-tone";
import { Button } from "../../ui/button";
import { DocumentStatusBadge } from "../../ui/DocumentStatusBadge";
import { cn } from "@/lib/utils";

interface Props {
  node: OrderChainNodeDto;
  /** Адрес перехода; null — строка не кликается (нет прав, нет ссылки). */
  href: string | null;
  /** Повторная загрузка цепочки — для узла, чей источник не ответил. */
  onRetry?: () => void;
}

export function OrderChainNode({ node, href, onRetry }: Props) {
  const state = nodeStateMeta(node.state);
  const owner = ownerMeta(nodeOwner(node));
  const detail = nodeDetailLine(node);
  const time = nodeTimeLine(node);
  const ratio = nodeProgressRatio(node);
  // Недоступный источник не делаем ссылкой целиком: внутри живёт «Повторить».
  const clickable = !!href && node.state !== "unavailable";

  const body = (
    <>
      {node.state === "current" ? (
        <span aria-hidden className={cn("absolute inset-y-0 left-0 w-1", owner.edge)} />
      ) : null}
      <ChainRail node={node} />
      <div className="flex min-w-0 flex-1 flex-col gap-1">
        <div className="flex flex-wrap items-center gap-x-2 gap-y-1">
          <span
            className={cn(
              "min-w-0 truncate text-sm font-medium",
              state.dimmed && "text-on-surface-variant",
            )}
          >
            {node.title}
          </span>
          {node.number && node.hidden !== "no_permission" ? (
            <span className="font-mono text-sm tabular-nums text-on-surface-variant">
              {node.number}
            </span>
          ) : null}
          <DocumentStatusBadge tone={resolveNodeTone(node)}>
            {node.statusLabel}
          </DocumentStatusBadge>
        </div>

        {detail ? (
          <p
            className={cn(
              "line-clamp-2 text-xs",
              state.dimmed ? "text-on-surface-muted" : "text-on-surface-variant",
            )}
            title={detail}
          >
            {detail}
          </p>
        ) : null}

        {ratio !== null ? (
          <span
            aria-hidden
            className="h-1.5 w-full max-w-60 overflow-hidden rounded-full bg-on-surface/8"
          >
            <span
              className="block h-full rounded-full bg-primary"
              style={{ width: `${Math.round(ratio * 100)}%` }}
            />
          </span>
        ) : null}

        {time ? (
          <p className="text-xs tabular-nums text-on-surface-muted">{time}</p>
        ) : null}

        {node.state === "unavailable" && onRetry ? (
          <div className="flex flex-wrap items-center gap-2 pt-1">
            <Button variant="outline" size="sm" className="min-h-11" onClick={onRetry}>
              Повторить
            </Button>
            {href ? (
              <Button variant="ghost" size="sm" className="min-h-11" asChild>
                <Link to={href}>Открыть документ</Link>
              </Button>
            ) : null}
          </div>
        ) : null}
      </div>
      {clickable ? (
        <ChevronRight className="mt-0.5 size-4 shrink-0 text-on-surface-muted" aria-hidden />
      ) : null}
    </>
  );

  const className = cn(
    "relative flex min-h-14 gap-3 overflow-hidden rounded-xl px-3 py-2.5 lg:min-h-12 lg:py-2",
    state.rowClass,
    clickable && "transition-colors hover:bg-surface-low",
  );

  return (
    <li>
      {clickable && href ? (
        // `<a>` в потоковом контексте — прозрачный контейнер: абзацы и полосу
        // прогресса внутрь класть можно, лишняя обёртка ломала бы flex-строку.
        <Link to={href} className={className}>
          {body}
        </Link>
      ) : (
        <div className={className}>{body}</div>
      )}
    </li>
  );
}

/** Рейл узла: ● пройден, ◉ текущий, ○ будущий, ⨯ не состоялся, ⚠ нет данных. */
function ChainRail({ node }: { node: OrderChainNodeDto }): ReactNode {
  const state = nodeStateMeta(node.state);
  const owner = ownerMeta(nodeOwner(node));
  if (state.rail === "failed") {
    return <X className={cn("mt-0.5 size-4 shrink-0", state.railClass)} aria-hidden />;
  }
  if (state.rail === "alert") {
    return (
      <TriangleAlert className={cn("mt-0.5 size-4 shrink-0", state.railClass)} aria-hidden />
    );
  }
  return (
    <span
      aria-hidden
      className={cn(
        "mt-1 size-3 shrink-0 rounded-full",
        state.rail === "outline" ? state.railClass : owner.dot,
        state.rail === "current" && "ring-4 ring-primary/20",
      )}
    />
  );
}
